import { Dialog, DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnInit, signal } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { portalConst } from '@app/config';
import { AlertService, LearningTrailsElementsService, NotificationService } from '@app/core';
import { StatusEnum } from '@app/core/models/status.enum';
import { ElementsService } from '@app/core/services/elements.service';
import { ModalAssessmentExecutionService } from '@app/modules/assessments-manager/shared/modal-assessment-execution/modal-assessment-execution.service';
import { ModalNewAssessmentService } from '@app/modules/assessments-manager/shared/modal-new-assessment/modal-new-assessment.service';
import { downloadFile, notNull } from '@app/shared/utils';
import { TranslateService } from '@ngx-translate/core';
import { DropzoneConfigInterface } from 'ngx-dropzone-wrapper';
import { BehaviorSubject, filter, finalize, take } from 'rxjs';
import { Types } from '../../utils/type-mapper';
import { ModalDocPreviewComponent } from '../modal-doc-preview/modal-doc-preview.component';

@Component({
  templateUrl: './modal-learning-form-element.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ModalLearningFormElementComponent implements OnInit {
  private readonly _dialog = inject(Dialog);
  private readonly _dialogRef = inject(DialogRef);
  private readonly _cdRef = inject(ChangeDetectorRef);
  private readonly _formBuilder = inject(FormBuilder);
  private readonly _alertService = inject(AlertService);
  private readonly _elementsService = inject(ElementsService);
  private readonly _translateService = inject(TranslateService);
  private readonly _notificationService = inject(NotificationService);
  private readonly _modalNewAssessmentService = inject(ModalNewAssessmentService);
  private readonly _learningTrailsElementsService = inject(LearningTrailsElementsService);
  private readonly _modalAssessmentExecutionService = inject(ModalAssessmentExecutionService);
  public readonly data = inject(DIALOG_DATA);
  public readonly ckeditorUploadImageUrl = `${portalConst.api.usuario}/v2/upload/ckeditor-image?module=trilhas`;
  public types = [];
  public form: FormGroup;
  public configUpload: DropzoneConfigInterface;
  public submitted$ = new BehaviorSubject<boolean>(false);
  private _element$ = new BehaviorSubject<any>(undefined);
  public showPublish = signal<boolean>(true);
  public loading = signal<boolean>(false);

  constructor() {
    this._elementsService.status$.subscribe((status) => {
      this.showPublish.set(
        (!!this.data.hasEvent && !!status && !!this.data.trail.curso.status) ||
          !!this.form?.value.evento_historico_curso?.length,
      );
    });
  }

  public ngOnInit(): void {
    this.initTypes();
    this.initForm();
    this.initConfigUpload();

    this.showPublish.set(
      (!!this.data.hasEvent && !!this.data.trail.status && !!this.data.trail.curso.status) ||
        !!this.form.value.evento_historico_curso?.length,
    );

    if (!!this.data?.data) {
      this.form.patchValue({
        ...this.data.data,
        iframe: !!this.data.data.iframe,
      });

      this.form.get('addHistoricoEvento').setValue(!!this.form.value.evento_historico_curso?.length);
      if (!!this.form.value.evento_historico_curso?.length) {
        this.form.controls.addHistoricoEvento.disable();
      }
    } else {
      this.form.get('sessao_id').setValue(this.data.section_id);
      this.form.get('ordem').setValue(this.data.order);
    }
  }

  public get isEdit(): boolean {
    return !!this.form.value.id;
  }

  public isCurrentType(type: string): boolean {
    return type === this.form.value.tipo;
  }

  public onSubmit(): void {
    this.loading.set(true);
    this.submitted$.next(true);
    this.form.markAllAsTouched();
    this.form.markAsPristine();
    if (this.form.valid) {
      if (!!this.form.value.id) {
        if (!this.form.value.status && !this.form.value.addHistoricoEvento && this.data.hasEvent) {
          this._alertService
            .confirm({ message: this._translateService.instant(`trilhas.estudio.trilha.sem-publicar`) })
            .pipe(
              notNull(),
              finalize(() => this.loading.set(false)),
            )
            .subscribe(() => this.update());
        } else {
          this.update();
        }
      } else {
        this.save();
      }
    }
  }

  public save(): void {
    this.setIconByContent();
    this._learningTrailsElementsService
      .store(this.form.value, false)
      .pipe(finalize(() => this.submitted$.next(false)))
      .subscribe({
        next: (resp) => this.afterSave(resp),
        error: () => this.afterSave(this._translateService.instant('geral.erro-ao-salvar')),
      });
  }

  public update(): void {
    this.setIconByContent();
    this._learningTrailsElementsService
      .update(this.form.value.id, this.form.value)
      .pipe(finalize(() => this.submitted$.next(false)))
      .subscribe({
        next: (resp) => this.afterSave(resp),
        error: () => this.afterSave(this._translateService.instant('geral.erro-ao-salvar')),
      });
  }

  private setIconByContent() {
    const tipo = this.form.value.tipo;
    const hasEmbed = this.form.value.conteudo?.includes('raw-html-embed');

    if (tipo === Types.texto && hasEmbed) {
      this.form.get('tipo')?.setValue(Types.embed);
    } else if (tipo === Types.embed && !hasEmbed) {
      this.form.get('tipo')?.setValue(Types.texto);
    }
  }

  public onUpload(attachment: any): void {
    const hashs = this.form.value.hash_anexos ?? [];
    hashs.push(attachment.hash);
    this.form.get('hash_anexos').setValue(hashs);
    const data = this.form.value.anexos ?? [];
    data.push({
      hash: attachment.hash,
      nome_arquivo: attachment.title,
      extensao: attachment.extension,
      size: attachment.size,
      file: attachment.file,
      url: `${portalConst.s3_path_temp}${attachment.file}`,
    });
    this.form.get('anexos').setValue(data);
  }

  public onDelete(index: number): void {
    const anexos = this.form.value.anexos;
    const hashs = this.form.value.hash_anexos;
    if (!!anexos[index]?.id) {
      if (hashs?.length) {
        const newIndex = hashs.indexOf(anexos[index].hash);
        if (newIndex !== -1) {
          hashs.splice(newIndex, 1);
          this.form.get('hash_anexos').setValue(hashs);
        }
      }
      anexos.splice(index, 1);
      this.form.get('anexos').setValue(anexos);
    }
  }

  public onDownload(file: any): void {
    downloadFile(file);
  }

  public onOpenPreviewDoc(attachment: any): void {
    this._dialog.open(ModalDocPreviewComponent, {
      data: attachment,
      width: '90vw',
      height: '75vh',
    });
  }

  public onChangeType(value: string): void {
    if (value === this.form.value.tipo || !!this.form.value.id) {
      return;
    }

    this.form.reset({
      tipo: value,
    });

    this.form.markAsUntouched();

    if (!this.data?.data) {
      this.form.patchValue({
        sessao_id: this.data.section_id,
        ordem: this.data.order,
      });
    }

    if (value === 'avaliacao' && !this.form.value.quiz_uuid) {
      this.onOpenQuiz();
    } else {
      this.form.get('tipo').setValue(value);
    }
  }

  public get currentType() {
    return !!this.form.value.tipo ? this.types.find((t) => t.type === this.form.value.tipo) : null;
  }

  public onOpenQuiz() {
    this._modalNewAssessmentService.open({
      aplicacao_uuid: this.form.value.quiz_uuid,
      extra: {
        trilha_elemento: {
          id: this.form.value.id,
          sessao_id: this.form.value.sessao_id,
          ordem: this.form.value.ordem,
          evento_historico_curso: this.form.value.evento_historico_curso,
        },
      },
    });

    this._modalNewAssessmentService.closed$
      .pipe(
        take(1),
        filter((state) => !!state?.aplicacao?.uuid),
      )
      .subscribe((state: any) => {
        this.form.patchValue({
          titulo: state?.avaliacao?.titulo,
          quiz_uuid: state?.aplicacao?.uuid,
          sessao_id: state?.extra?.trilha_elemento?.sessao_id ?? this.form.value.sessao_id,
          ordem: state?.extra?.trilha_elemento?.ordem ?? this.form.value.ordem,
        });
        this.form.get('tipo').setValue('avaliacao');
        this._cdRef.markForCheck();
      });
  }

  public onOpenPreviewQuiz(quizUuid: string) {
    if (!quizUuid) {
      return;
    }

    this._modalAssessmentExecutionService.open({
      data: { aplicacao_uuid: quizUuid },
      extra: { preview: true },
    });
  }

  public onClose(): void {
    this._dialogRef.close();
  }

  private afterSave(resp: any) {
    if (resp.ret === StatusEnum.Active) {
      this.form.get('id').setValue(resp.id);
      if (!!resp.anexo_principal) {
        this.form.get('anexo_principal').setValue({ ...resp.anexo_principal, hash: undefined });
      }

      if (!!resp.anexos) {
        const attachment = [];
        resp.anexos.forEach((anexo) => {
          attachment.push(anexo);
        });
        this.form.get('anexos').setValue(attachment);
      }
      if (!!resp?.evento_historico_curso) {
        const evento_historico_curso = [];
        resp.evento_historico_curso?.forEach((evento) => {
          evento_historico_curso.push(evento);
        });
        this.form.get('evento_historico_curso').setValue(evento_historico_curso);
        this.form.controls.addHistoricoEvento.disable();
        this._cdRef.markForCheck();
      }
      this._element$.next(this.form.value);
      this._notificationService.success(resp.msg);
      this._dialogRef.close(this._element$.getValue());
    } else {
      this._notificationService.error(resp);
      this._dialogRef.close();
    }
  }

  private initForm(): void {
    this.form = this._formBuilder.group({
      id: undefined,
      sessao_id: [undefined, Validators.required],
      tipo: ['video', Validators.required],
      titulo: [undefined, [Validators.required, Validators.maxLength(120), Validators.minLength(3)]],
      conteudo: undefined,
      ordem: [0, Validators.required],
      anexo_principal: [undefined, Validators.required],
      quiz_uuid: undefined,
      created_at: undefined,
      hash_anexos: [],
      anexos: [],
      evento_historico_curso: [],
      addHistoricoEvento: false,
      iframe: false,
    });
    this.form.controls.titulo.valueChanges.subscribe(() => {
      this.form.controls.titulo.markAsTouched();
      this.form.controls.titulo.markAsPristine();
    });

    this.form.controls.anexo_principal.valueChanges.pipe(notNull()).subscribe((value) => {
      if (this.form.valid && this.form.value.tipo === 'video' && !!value.video && !!value.vimeo_hash) {
        this._element$.next({ ...this.form.value, anexo_principal: value });
      }
    });

    this.form.controls.tipo.valueChanges.pipe(notNull()).subscribe((value) => {
      this.submitted$.next(false);
      if (value === 'texto') {
        this.form.get('anexo_principal').clearValidators();
        this.form.get('conteudo').setValidators(Validators.required);
      } else if (value === 'avaliacao') {
        this.form.get('anexo_principal').clearValidators();
        this.form.get('conteudo').clearValidators();
      } else if (value === 'embed') {
        this.form.get('anexo_principal').clearValidators();
        this.form.get('conteudo').setValidators(Validators.required);
      } else {
        this.form.get('anexo_principal').setValidators(Validators.required);
        this.form.get('conteudo').clearValidators();
      }
      this.form.get('anexo_principal').updateValueAndValidity();
      this.form.get('conteudo').updateValueAndValidity();
    });
  }

  private initConfigUpload(): void {
    this.configUpload = {
      clickable: true,
      url: `${portalConst.api.usuario}/temp-upload/v1/file`,
      params: {
        pesoMaximo: 70000,
        stringKbMb: 'Mb',
        anexo: true,
      },
      createImageThumbnails: false,
      maxFiles: 10,
      maxFilesize: 70,
      acceptedFiles: '.doc,.docx,.ppt,.pptx,.xls,.xlsx,.pdf,.jpg,.jpeg,.png',
    };
  }

  private initTypes(): void {
    this.types = [
      {
        title: this._translateService.instant('trilhas.estudio.trilha.tipo-elemento.audio'),
        file: 'audio-types',
        type: 'audio',
        style: {
          display: 'flex',
          text: 'text-base-trilhas-tipo-audio',
          bg: '!bg-base-trilhas-tipo-audio',
          hover: 'hover:bg-base-trilhas-tipo-audio',
          border: 'border-base-trilhas-tipo-audio',
        },
      },
      {
        title: this._translateService.instant('trilhas.estudio.trilha.tipo-elemento.e-book'),
        file: 'doc-types',
        type: 'flip',
        style: {
          display: 'flex',
          text: 'text-base-trilhas-tipo-ebook',
          bg: '!bg-base-trilhas-tipo-ebook',
          hover: 'hover:bg-base-trilhas-tipo-ebook',
          border: 'border-base-trilhas-tipo-ebook',
        },
      },
      {
        title: this._translateService.instant('trilhas.estudio.trilha.tipo-elemento.imagem'),
        file: 'image-types',
        type: 'imagem',
        style: {
          display: 'flex',
          text: 'text-base-trilhas-tipo-image',
          bg: '!bg-base-trilhas-tipo-image',
          hover: 'hover:bg-base-trilhas-tipo-image',
          border: 'border-base-trilhas-tipo-image',
        },
      },
      {
        title: this._translateService.instant('trilhas.estudio.trilha.tipo-elemento.link'),
        file: 'link-types',
        type: 'link',
        style: {
          display: 'flex',
          text: 'text-base-trilhas-tipo-link',
          bg: '!bg-base-trilhas-tipo-link',
          hover: 'hover:bg-base-trilhas-tipo-link',
          border: 'border-base-trilhas-tipo-link',
        },
      },
      {
        title: this._translateService.instant('trilhas.estudio.trilha.tipo-elemento.texto'),
        file: 'text-types',
        type: 'texto',
        style: {
          display: 'flex',
          text: 'text-base-trilhas-tipo-text',
          bg: '!bg-base-trilhas-tipo-text',
          hover: 'hover:bg-base-trilhas-tipo-text',
          border: 'border-base-trilhas-tipo-text',
        },
      },
      {
        title: this._translateService.instant('trilhas.estudio.trilha.tipo-elemento.video'),
        file: 'video-types',
        type: 'video',
        style: {
          display: 'flex',
          text: 'text-base-trilhas-tipo-video',
          bg: '!bg-base-trilhas-tipo-video',
          hover: 'hover:bg-base-trilhas-tipo-video',
          border: 'border-base-trilhas-tipo-video',
        },
      },
      {
        title: this._translateService.instant('trilhas.estudio.trilha.tipo-elemento.quiz'),
        file: 'gestor-avaliacoes',
        type: 'avaliacao',
        style: {
          display: 'flex',
          text: 'text-base-trilhas-tipo-quiz',
          bg: '!bg-base-trilhas-tipo-quiz',
          hover: 'hover:bg-base-trilhas-tipo-quiz',
          border: 'border-base-trilhas-tipo-quiz',
        },
      },
      {
        title: this._translateService.instant('trilhas.estudio.trilha.tipo-elemento.inserido'),
        file: 'piece',
        type: 'embed',
        style: {
          display: 'hidden',
          text: 'text-base-trilhas-tipo-embed',
          bg: '!bg-base-trilhas-tipo-embed',
          hover: 'hover:bg-base-trilhas-tipo-embed',
          border: 'border-base-trilhas-tipo-embed',
        },
      },
    ];
  }
}
